import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import Cookies from 'js-cookie';
import { supabase } from '../supabaseClient';

interface Journal {
  id: string;
  content: string;
  updated_at: string;
  sentiment: number | null;
  emotions: string[] | null;
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  currentUser: User | null;
  loading: boolean;
  login: (user: User) => void;
  logout: () => void;
  hasJournaledToday: boolean;
  setHasJournaledToday: (status: boolean) => void;
  streak: number;
  fetchUserStats: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasJournaledToday, setHasJournaledToday] = useState(false);
  const [streak, setStreak] = useState(0);

  const calculateAndSetStreak = (journals: { updated_at: string }[]) => {
    if (!journals || journals.length === 0) {
      setStreak(0);
      return;
    }

    const journalDates = Array.from(new Set(journals.map(j => new Date(j.updated_at).toDateString())))
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => b.getTime() - a.getTime());

    let currentStreak = 0;
    let today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate = journalDates[0];

    const diffDays = (date1: Date, date2: Date) => {
      const diffTime = Math.abs(date2.getTime() - date1.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    if (diffDays(today, startDate) > 1) {
      setStreak(0);
      return;
    }

    currentStreak = 1;
    let lastDate = startDate;

    for (let i = 1; i < journalDates.length; i++) {
      const currentDate = journalDates[i];
      if (diffDays(lastDate, currentDate) === 1) {
        currentStreak++;
        lastDate = currentDate;
      } else {
        break; 
      }
    }

    setStreak(currentStreak);
  };

  const fetchUserStats = useCallback(async () => {
    if (!currentUser) return;

    const { data, error } = await supabase
      .from('journals')
      .select('updated_at')
      .eq('user_id', currentUser.id)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching user stats:', error);
    } else {
      const today = new Date().toDateString();
      const hasJournaled = (data || []).some(j => new Date(j.updated_at).toDateString() === today);
      setHasJournaledToday(hasJournaled);
      calculateAndSetStreak(data || []);
    }
  }, [currentUser]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const sessionCookie = Cookies.get('session_token');
        const storedUser = localStorage.getItem('currentUser');
        if (sessionCookie && storedUser) {
          setCurrentUser(JSON.parse(storedUser));
        }
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchUserStats();
    }
  }, [currentUser, fetchUserStats]);

  const login = (user: User) => {
    Cookies.set('session_token', 'true', { expires: 1 }); // Expires in 1 day (24 hours)
    localStorage.setItem('currentUser', JSON.stringify(user));
    setCurrentUser(user);
  };

  const logout = () => {
    Cookies.remove('session_token');
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated: !!currentUser, currentUser, loading, login, logout, hasJournaledToday, setHasJournaledToday, streak, fetchUserStats }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
